import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";

const TermsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | Hireun</title>
        <meta name="description" content="Hireun Terms of Service - Read our terms and conditions for using the interview preparation platform." />
        <link rel="canonical" href="https://hireun.com/terms" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: January 2025</p>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing and using Hireun ("the Service"), you accept and agree to be bound by the 
              terms and provision of this agreement. If you do not agree to abide by these terms, 
              please do not use this service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              Hireun is an AI-powered interview preparation platform that helps users prepare for 
              job interviews by analyzing their CVs and job descriptions to generate personalized 
              interview content, questions, and answers.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. User Accounts</h2>
            <p className="text-gray-700 mb-4">
              To access certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. User Content</h2>
            <p className="text-gray-700 mb-4">
              You retain ownership of all content you upload to Hireun, including your CV and job 
              descriptions. By uploading content, you grant us a limited license to process this 
              content solely for the purpose of providing the Service.
            </p>
            <p className="text-gray-700 mb-4">
              You represent and warrant that you have all necessary rights to upload and share 
              the content you provide, and that such content does not violate any third party's 
              intellectual property rights.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. AI-Generated Content</h2>
            <p className="text-gray-700 mb-4">
              The interview preparation content generated by Hireun is created using artificial 
              intelligence and is intended as guidance only. We do not guarantee:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>The accuracy or completeness of generated content</li>
              <li>That using our content will result in job offers</li>
              <li>That generated answers are optimal for any specific interview</li>
            </ul>
            <p className="text-gray-700 mb-4">
              Users should review and customize all generated content before use in actual interviews.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Subscription and Payment</h2>
            <p className="text-gray-700 mb-4">
              Premium features require a paid subscription. By subscribing:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>You authorize us to charge your payment method on a recurring basis</li>
              <li>You can cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>We do not provide refunds for partial billing periods</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Prohibited Uses</h2>
            <p className="text-gray-700 mb-4">
              You agree not to use the Service to:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Upload malicious content or attempt to compromise the Service</li>
              <li>Resell or redistribute generated content commercially</li>
              <li>Create multiple accounts to circumvent usage limits</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              The Service, including its original content, features, and functionality, is owned by 
              Hireun and is protected by international copyright, trademark, and other intellectual 
              property laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">
              To the maximum extent permitted by law, Hireun shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of 
              the Service.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Changes to Terms</h2>
            <p className="text-gray-700 mb-4">
              We reserve the right to modify these terms at any time. We will notify users of 
              significant changes via email or through the Service. Continued use after changes 
              constitutes acceptance of the modified terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These terms shall be governed by and construed in accordance with the laws of France, 
              without regard to its conflict of law provisions.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Contact</h2>
            <p className="text-gray-700 mb-4">
              For questions about these Terms of Service, please contact us at:{" "}
              <a href="mailto:contact@hireun.com" className="text-blue-600 hover:underline">
                contact@hireun.com
              </a>
            </p>
          </div>
        </div>

        <PublicFooter />
      </div>
    </>
  );
};

export default TermsPage;
